// EPISTEME — Core epistemic logic (pure, testable, client-only)
// Bayesian-lite updates, process scoring, sufficiency evaluation, calibration helpers.

import type { Audit, CredibilityScores, EvidenceItem, ProcessCheck, SufficiencyCriteria } from './types';

export function averageCredibility(e: EvidenceItem): number {
  const c = e.credibility;
  return (c.expertise + c.independence + c.directness + c.recency + c.consistency) / 5;
}

export function evidenceImpactScore(e: EvidenceItem): number {
  const base = averageCredibility(e);
  const dir = e.support === 'pro' ? 1 : e.support === 'con' ? -1 : 0.3;
  const w = Math.max(0.3, Math.min(3, e.weight || 1));
  return base * dir * w * 1.8; // scaled for human-visible movement
}

// Proper Bayesian update using explicit likelihoodRatio when provided.
// Falls back to legacy impact score for backward compat.
export function updateCredence(current: number, evidence: EvidenceItem[]): number {
  if (evidence.length === 0) return current;

  let odds = current / (100 - Math.max(0.1, Math.min(99.9, current)));

  for (const e of evidence) {
    let lr: number;
    if (e.likelihoodRatio && e.likelihoodRatio > 0) {
      lr = e.likelihoodRatio;
      // direction: if support is con, invert
      if (e.support === 'con') lr = 1 / lr;
      if (e.support === 'mixed') lr = Math.sqrt(lr); // dampen
    } else {
      const impact = evidenceImpactScore(e);
      lr = Math.max(0.18, Math.min(5.5, 1 + (impact / 18)));
    }
    odds = odds * lr;
  }

  let posterior = (odds / (1 + odds)) * 100;
  return Math.max(3, Math.min(97, Math.round(posterior)));
}

export function applyLikelihoodRatio(currentCredence: number, lr: number): number {
  if (lr <= 0) return currentCredence;
  let odds = currentCredence / (100 - currentCredence);
  odds = odds * lr;
  return Math.max(3, Math.min(97, Math.round((odds / (1 + odds)) * 100)));
}

// Compute a simple "epistemic hygiene / process quality" score 0-100
export function computeProcessScore(audit: Audit): number {
  let score = 42; // base for having started

  const ev = audit.evidence;
  if (ev.length >= 3) score += 8;
  if (ev.length >= 6) score += 7;

  const hasCon = ev.some(e => e.support === 'con');
  if (hasCon) score += 14;

  const avgCred = ev.length > 0 ? ev.reduce((s, e) => s + averageCredibility(e), 0) / ev.length : 0;
  if (avgCred > 3.2) score += 9;

  const unc = audit.uncertainty;
  if (unc.alternatives.length >= 2) score += 10;
  if (unc.assumptions.length >= 2) score += 6;

  if (audit.trust.nodes.length >= 2) score += 5;

  const suf = audit.sufficiency;
  if (suf.textual && suf.textual.length > 40) score += 11;
  if (suf.minSources >= 2) score += 4;

  if (audit.decision) score += 8; // committed with rationale

  if (audit.revision.falsifiers.length >= 1) score += 6;
  if (audit.revision.monitors.length >= 1) score += 4;

  // Process checks (user explicitly reflected)
  const checks = audit.processChecks;
  if (checks.length > 0) {
    const last = checks[checks.length - 1];
    if (last.soughtDisconfirmers) score += 6;
    if (last.wroteAlternatives) score += 5;
    if (last.preCommitted) score += 7;
    if (last.consideredBaseRate) score += 4;
    // penalize high affect / fatigue a bit
    score -= Math.max(0, last.affectCheck - 2) * 1.5;
    score -= Math.max(0, last.fatigue - 3) * 1.2;
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

// Does the current state meet the user's own sufficiency bar?
export function meetsSufficiency(audit: Audit): boolean {
  const suf = audit.sufficiency;
  const ev = audit.evidence;

  const proStrong = ev.filter(e => e.support === 'pro' && averageCredibility(e) >= 3.0).length;
  const conStrong = ev.filter(e => e.support === 'con' && averageCredibility(e) >= 3.5).length;

  const currentCred = audit.uncertainty.credence;

  if (proStrong < suf.minSources) return false;
  if (currentCred < suf.minCredence) return false;
  if (conStrong > suf.maxDisconfirmer) return false;

  if (suf.requiredDays) {
    const oldestStrong = Math.min(
      ...ev.filter(e => e.support === 'pro' && averageCredibility(e) >= 3).map(e => Date.now() - (e.addedAt || Date.now()))
    );
    const daysOld = oldestStrong / (1000 * 3600 * 24);
    if (daysOld < suf.requiredDays) return false;
  }
  return true;
}

// Simple Monte Carlo outcome sampler for "if I act, what happens?"
// Returns array of simulated final "regret or success" scores.
export function runMonteCarlo(audit: Audit, trials = 1200): number[] {
  const results: number[] = [];
  const baseCred = audit.uncertainty.credence / 100;

  // crude variance from number & quality of evidence + alternatives considered
  const evQuality = audit.evidence.length > 0
    ? audit.evidence.reduce((s, e) => s + averageCredibility(e), 0) / audit.evidence.length
    : 2.0;

  const altPenalty = Math.min(0.35, audit.uncertainty.alternatives.length * 0.07);
  const variance = Math.max(0.09, 0.42 - (evQuality / 11) - altPenalty);

  for (let i = 0; i < trials; i++) {
    // sample a "true" probability from a beta-like distribution centered on current credence
    const alpha = Math.max(1.5, baseCred * (1 / variance));
    const beta = Math.max(1.5, (1 - baseCred) * (1 / variance));
    let p = sampleBeta(alpha, beta);

    // add some "unknown unknowns" and trust issues
    const trustNoise = (audit.trust.nodes.reduce((s, n) => s + (n.incentive + n.trackRecord) / 2, 0) / Math.max(1, audit.trust.nodes.length)) * -0.04;
    p = Math.max(0.02, Math.min(0.98, p + trustNoise));

    // outcome: if we act, "success" is 1 when reality aligns with claim
    const outcome = Math.random() < p ? 1 : -1;
    results.push(outcome);
  }
  return results;
}

// Very small beta sampler using simple method (good enough for viz)
function sampleBeta(alpha: number, beta: number): number {
  // crude but effective: sum of gammas approx via normals + transform
  const x = sampleGammaApprox(alpha);
  const y = sampleGammaApprox(beta);
  return x / (x + y);
}

function sampleGammaApprox(shape: number): number {
  // Marsaglia + fast path for shape > 1
  if (shape < 1) return sampleGammaApprox(shape + 1) * Math.pow(Math.random(), 1 / shape);
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  while (true) {
    let x = normal();
    let v = 1 + c * x;
    if (v <= 0) continue;
    v = v * v * v;
    const u = Math.random();
    if (u < 1 - 0.0331 * (x * x * x * x)) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

function normal(): number {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

export function summarizeMonteCarlo(results: number[]) {
  const successes = results.filter(r => r > 0).length;
  const p = successes / results.length;
  const mean = results.reduce((s, r) => s + r, 0) / results.length;
  return { pSuccess: p, expectedValue: mean, trials: results.length };
}

// Process check helper
export function createProcessCheck(partial: Partial<ProcessCheck> = {}): ProcessCheck {
  return {
    id: crypto.randomUUID(),
    checkedAt: Date.now(),
    soughtDisconfirmers: partial.soughtDisconfirmers ?? false,
    wroteAlternatives: partial.wroteAlternatives ?? false,
    preCommitted: partial.preCommitted ?? false,
    consideredBaseRate: partial.consideredBaseRate ?? false,
    affectCheck: partial.affectCheck ?? 2,
    fatigue: partial.fatigue ?? 2,
  };
}

// Quick calibration helper (for future dashboard)
export function computeCalibration(outcomes: { confidence: number; correct: boolean }[]): {
  bins: { bin: number; count: number; accuracy: number }[];
  overall: number;
} {
  const bins = [0.1, 0.3, 0.5, 0.7, 0.9].map(b => ({ bin: b, count: 0, accuracy: 0 }));
  for (const o of outcomes) {
    const b = bins.findIndex(bb => o.confidence <= bb * 100) || 4;
    bins[b].count++;
    if (o.correct) bins[b].accuracy += 1;
  }
  bins.forEach(b => { if (b.count > 0) b.accuracy /= b.count; });
  const overall = outcomes.length ? outcomes.filter(o => o.correct).length / outcomes.length : 0;
  return { bins: bins.filter(b => b.count > 0), overall };
}

// Brier score (lower is better). Treats outcome as 1=correct / 0=incorrect.
export function computeBrierScore(outcomes: { confidence: number; correct: boolean }[]): number {
  if (outcomes.length === 0) return 0;
  let sum = 0;
  for (const o of outcomes) {
    const p = Math.max(0.01, Math.min(0.99, o.confidence / 100));
    const y = o.correct ? 1 : 0;
    sum += Math.pow(p - y, 2);
  }
  return sum / outcomes.length;
}

// Returns data suitable for a reliability diagram
export function getReliabilityData(outcomes: { confidence: number; correct: boolean }[]) {
  const bins = [10, 30, 50, 70, 90];
  const data = bins.map(mid => ({ mid, count: 0, avgConf: 0, accuracy: 0 }));
  for (const o of outcomes) {
    const idx = bins.findIndex(b => o.confidence <= b) === -1 ? 4 : bins.findIndex(b => o.confidence <= b);
    const d = data[idx];
    d.count += 1;
    d.avgConf += o.confidence;
    if (o.correct) d.accuracy += 1;
  }
  return data.map(d => ({
    mid: d.mid,
    count: d.count,
    avgConf: d.count ? d.avgConf / d.count : 0,
    accuracy: d.count ? d.accuracy / d.count : 0,
  })).filter(d => d.count > 0);
}

// === New core epistemology functions ===

// Simple Value of Information estimate using MC.
// Assumes binary action with payoffs: if claim true and act: +100, false and act: -80, etc.
// Returns approximate EV improvement from resolving uncertainty perfectly.
export function computeVoI(audit: Audit): { evImprovement: number; explanation: string } {
  const results = runMonteCarlo(audit, 800);
  const summary = summarizeMonteCarlo(results);
  const p = summary.pSuccess;

  // crude payoffs for acting on the claim
  const payoffActTrue = 100;
  const payoffActFalse = -80;
  const payoffNoAct = 0;

  const currentEV = p * payoffActTrue + (1-p) * payoffActFalse;
  const perfectEV = Math.max(p * payoffActTrue + (1-p) * payoffNoAct, (1-p) * payoffActFalse + p * payoffNoAct); // best decision with perfect info

  const improvement = Math.max(0, perfectEV - currentEV);
  return {
    evImprovement: Math.round(improvement),
    explanation: `Resolving the uncertainty perfectly could improve expected value by ~${Math.round(improvement)} units (rough model). Current implied p=${(p*100).toFixed(0)}%.`
  };
}

// Sensitivity: recompute credence if one evidence's LR or strength is changed
export function sensitivityAnalysis(audit: Audit, evidenceId: string, newLrOrImpact: number): number {
  const ev = audit.evidence.map(e => {
    if (e.id === evidenceId) {
      return { ...e, likelihoodRatio: newLrOrImpact > 0 ? newLrOrImpact : undefined, weight: newLrOrImpact };
    }
    return e;
  });
  return updateCredence(audit.uncertainty.credence, ev);
}

// Hardcoded useful base rates / reference classes (local, no network)
export const BASE_RATES: Record<string, {domain: string, rate: string, sourceNote: string}> = {
  'startup_success': { domain: 'money', rate: '~10-20% of seed startups return >3x to investors', sourceNote: 'Industry aggregates (CB Insights, etc.)' },
  'medical_diagnosis_error': { domain: 'health', rate: '10-15% of diagnoses have significant error', sourceNote: 'Studies on diagnostic error' },
  'job_offer_accept_regret': { domain: 'work', rate: '~30-40% of people report significant regret within 2 years', sourceNote: 'Survey data' },
  'relationship_break_3yr': { domain: 'family', rate: '~40-50% of cohabiting relationships end within 3 years', sourceNote: 'Demographic stats' },
  'neighborhood_burglary': { domain: 'safety', rate: '1-3% per year for typical suburban home', sourceNote: 'US crime stats' },
};

export function suggestBaseRates(domain: string): Array<{key: string, rate: string}> {
  return Object.entries(BASE_RATES)
    .filter(([_, v]) => v.domain === domain || domain === 'all')
    .map(([k, v]) => ({ key: k, rate: v.rate }));
}
